import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Copy, AlertTriangle, Plus, RefreshCw } from 'lucide-react';
import { inventoryTemplatesAPI } from '../lib/api';
import { toast } from 'sonner';

export const CloneInventoryModal = ({ open, onClose, targetVehicleId, targetVehicleName, allVans }) => {
  const [sourceVehicleId, setSourceVehicleId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mode, setMode] = useState('add_missing');
  const [confirmStep, setConfirmStep] = useState(false);
  const [applying, setApplying] = useState(false);

  // Filter out current vehicle from available sources
  const availableVehicles = (allVans || []).filter(v => v.id !== targetVehicleId && v.is_active);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSourceVehicleId('');
      setPreview(null);
      setMode('add_missing');
      setConfirmStep(false);
    }
  }, [open]);

  // Fetch preview when source selected
  useEffect(() => {
    if (sourceVehicleId) {
      fetchPreview(sourceVehicleId);
    } else {
      setPreview(null);
    }
    setConfirmStep(false);
  }, [sourceVehicleId]);

  // Reset confirm on mode change
  useEffect(() => {
    setConfirmStep(false);
  }, [mode]);

  const fetchPreview = async (vehicleId) => {
    setPreviewLoading(true);
    try {
      const res = await inventoryTemplatesAPI.clonePreview(vehicleId);
      setPreview(res.data);
    } catch {
      toast.error('Failed to load vehicle inventory preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    setApplying(true);
    try {
      const res = await inventoryTemplatesAPI.cloneApply({
        source_vehicle_id: sourceVehicleId,
        target_vehicle_id: targetVehicleId,
        mode,
      });
      const d = res.data;
      if (mode === 'add_missing') {
        toast.success(`Added ${d.items_added} items from ${d.source_vehicle_name}${d.items_skipped > 0 ? ` (${d.items_skipped} already existed)` : ''}`);
      } else {
        toast.success(`Replaced inventory on ${d.target_vehicle_name} with ${d.items_added} items from ${d.source_vehicle_name}`);
      }
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone inventory');
    } finally {
      setApplying(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) onClose(false);
  };

  const confirmMessage = mode === 'add_missing'
    ? `This will add ${preview?.item_count || 0} items from ${preview?.vehicle_name || 'source'} to ${targetVehicleName}. Existing items will not be modified.`
    : `This will replace ALL current inventory on ${targetVehicleName} with ${preview?.item_count || 0} items from ${preview?.vehicle_name || 'source'}.`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="clone-inventory-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Clone Inventory from Vehicle
          </DialogTitle>
          <DialogDescription>
            Copy inventory from another vehicle to <strong>{targetVehicleName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Source Vehicle Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Source Vehicle *</Label>
            {availableVehicles.length === 0 ? (
              <div className="text-sm text-muted-foreground py-3 text-center bg-muted/50 rounded-lg">
                No other active vehicles found in this account.
              </div>
            ) : (
              <Select value={sourceVehicleId} onValueChange={setSourceVehicleId}>
                <SelectTrigger className="h-11" data-testid="clone-source-select">
                  <SelectValue placeholder="Choose source vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {availableVehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}{v.van_number ? ` (#${v.van_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Preview */}
          {sourceVehicleId && (
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Preview</Label>
              {previewLoading ? (
                <div className="flex items-center gap-2 py-4 text-muted-foreground justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading inventory...
                </div>
              ) : preview ? (
                <div className="border rounded-lg" data-testid="clone-preview">
                  <div className="px-3 py-2 bg-muted/50 border-b flex items-center justify-between">
                    <span className="font-semibold text-sm">{preview.vehicle_name}</span>
                    <span className="text-sm text-muted-foreground">{preview.item_count} items</span>
                  </div>
                  {preview.items && preview.items.length > 0 && (
                    <div className="divide-y divide-border max-h-48 overflow-y-auto">
                      {preview.items.map((item) => (
                        <div key={item.item_id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium truncate block">{item.item_name}</span>
                            {item.item_sku && (
                              <span className="text-xs text-muted-foreground font-mono">{item.item_sku}</span>
                            )}
                          </div>
                          <span className="font-mono font-bold ml-3 shrink-0">{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Apply Mode */}
          {preview && preview.item_count > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Apply Mode *</Label>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    mode === 'add_missing' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setMode('add_missing')}
                  data-testid="clone-mode-add-missing"
                >
                  <Plus className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Add missing items only</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Only adds items not already on this vehicle. Existing quantities stay unchanged.</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    mode === 'replace' ? 'border-destructive bg-destructive/5' : 'border-border hover:border-destructive/50'
                  }`}
                  onClick={() => setMode('replace')}
                  data-testid="clone-mode-replace"
                >
                  <RefreshCw className="w-5 h-5 mt-0.5 shrink-0 text-destructive" />
                  <div>
                    <p className="font-semibold text-sm">Replace current inventory</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Removes all current inventory and replaces it with the source vehicle's inventory.</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Confirmation */}
          {confirmStep && (
            <div className={`p-3 rounded-lg border ${mode === 'replace' ? 'bg-destructive/5 border-destructive/30' : 'bg-primary/5 border-primary/30'}`} data-testid="clone-confirmation">
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${mode === 'replace' ? 'text-destructive' : 'text-primary'}`} />
                <p className="text-sm font-medium">{confirmMessage}</p>
              </div>
            </div>
          )}

          {/* Apply Button */}
          {preview && preview.item_count > 0 && (
            <Button
              className={`w-full h-11 font-bold ${confirmStep && mode === 'replace' ? 'bg-destructive hover:bg-destructive/90' : ''}`}
              onClick={handleApply}
              disabled={applying || !sourceVehicleId}
              data-testid="clone-apply-btn"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : confirmStep ? (
                <AlertTriangle className="w-4 h-4 mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {confirmStep ? 'Confirm & Clone' : 'Clone Inventory'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
